/**
 * Token Balance Display Component (Tier 5.1.5)
 * 
 * Shows user's token balance in the header with:
 * - Current balance display
 * - Low balance warning indicator
 * - Quick actions (purchase, history)
 * - Real-time updates via Firestore listener
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCoins, 
  faPlus, 
  faHistory, 
  faExclamationTriangle,
  faChevronDown,
  faStore
} from '@fortawesome/free-solid-svg-icons';
import { doc, onSnapshot } from 'firebase/firestore';
import { firestore } from '../FirebaseConfig';
import { useNavigate } from 'react-router-dom';
import '../styles/TokenBalance.css';

const LOW_BALANCE_THRESHOLD = 10;

/**
 * Main Token Balance Component for Header
 */
function TokenBalance({ userId, onPurchaseClick, compact = false }) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [recentChange, setRecentChange] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  
  const previousBalance = useRef(null);

  // Subscribe to balance updates
  useEffect(() => {
    if (!userId) {
      setBalance(null);
      setLoading(false);
      return;
    }

    const userRef = doc(firestore, 'users', userId);
    
    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.data();
        const newBalance = userData.tokenBalance || 0;
        
        // Check for balance change animation
        if (previousBalance.current !== null && previousBalance.current !== newBalance) {
          const change = newBalance - previousBalance.current;
          setRecentChange(change);
          
          // Clear change indicator after animation
          setTimeout(() => setRecentChange(null), 2000);
        }
        
        previousBalance.current = newBalance;
        setBalance(newBalance);
      } else {
        setBalance(0);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error subscribing to balance:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const handlePurchaseClick = useCallback(() => {
    setShowDropdown(false);
    if (onPurchaseClick) {
      onPurchaseClick();
    } else {
      navigate('/tokens?src=header-balance&intent=topup');
    }
  }, [onPurchaseClick, navigate]);

  const handleHistoryClick = useCallback(() => {
    setShowDropdown(false);
    navigate('/tokens?tab=history');
  }, [navigate]);

  const handleRedeemClick = useCallback(() => {
    setShowDropdown(false);
    navigate('/tokens?tab=offers');
  }, [navigate]);

  if (!userId) {
    return null;
  }

  const isLowBalance = balance !== null && balance < LOW_BALANCE_THRESHOLD;

  // Compact version for mobile
  if (compact) {
    return (
      <button 
        className={`token-balance-compact ${isLowBalance ? 'low' : ''}`}
        onClick={() => navigate('/tokens?src=header-balance&intent=topup')}
        title={`${balance ?? 0} Doubloons`}
      >
        <FontAwesomeIcon icon={faCoins} />
        <span>{loading ? '...' : balance}</span>
        {isLowBalance && <span className="low-indicator" />}
      </button>
    );
  }

  return (
    <div className="token-balance-container" ref={dropdownRef}>
      <button 
        className={`token-balance-btn ${isLowBalance ? 'low' : ''} ${showDropdown ? 'active' : ''}`}
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <div className="balance-icon">
          <FontAwesomeIcon icon={faCoins} />
          {isLowBalance && (
            <span className="warning-badge">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </span>
          )}
        </div>
        
        <div className="balance-info">
          <span className="balance-label">Doubloons</span>
          <span className={`balance-value ${recentChange ? (recentChange > 0 ? 'increase' : 'decrease') : ''}`}>
            {loading ? '...' : balance?.toLocaleString()}
            {recentChange && (
              <span className={`change-indicator ${recentChange > 0 ? 'positive' : 'negative'}`}>
                {recentChange > 0 ? '+' : ''}{recentChange}
              </span>
            )}
          </span>
        </div>
        
        <FontAwesomeIcon icon={faChevronDown} className="dropdown-icon" />
      </button>

      {showDropdown && (
        <div className="token-dropdown">
          <div className="dropdown-header">
            <div className="dropdown-balance">
              <FontAwesomeIcon icon={faCoins} />
              <span className="dropdown-balance-value">{balance?.toLocaleString()}</span>
              <span className="dropdown-balance-label">Doubloons available</span>
            </div>
            
            {isLowBalance && (
              <div className="low-balance-warning">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>Low balance. Top up now so you can redeem without interruption.</span>
              </div>
            )}
          </div>
          
          <div className="dropdown-actions">
            <button className="dropdown-action buy" onClick={handlePurchaseClick}>
              <FontAwesomeIcon icon={faPlus} />
              <span>Top Up Doubloons</span>
            </button>
            
            <button className="dropdown-action redeem" onClick={handleRedeemClick}>
              <FontAwesomeIcon icon={faStore} />
              <span>Redeem</span>
            </button>
            
            <button className="dropdown-action history" onClick={handleHistoryClick}>
              <FontAwesomeIcon icon={faHistory} />
              <span>History</span>
            </button>
          </div>
          
          <div className="dropdown-footer">
            <button className="view-all-link" onClick={() => { setShowDropdown(false); navigate('/tokens?src=header-balance&intent=manage'); }}>
              Manage all Doubloons →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Inline Token Display (for use within other components)
 */
export function TokenBalanceInline({ userId, size = 'medium', showLabel = true }) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setBalance(null);
      setLoading(false);
      return;
    }

    const userRef = doc(firestore, 'users', userId);
    
    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      if (snapshot.exists()) {
        setBalance(snapshot.data().tokenBalance || 0);
      } else {
        setBalance(0);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  return (
    <span className={`token-balance-inline ${size}`}>
      <FontAwesomeIcon icon={faCoins} />
      <span className="inline-value">{loading ? '...' : balance?.toLocaleString()}</span>
      {showLabel && <span className="inline-label">Doubloons</span>}
    </span>
  );
}

/**
 * Token Cost Display (for showing prices)
 */
export function TokenCost({ amount, size = 'medium', strikethrough = false }) {
  return (
    <span className={`token-cost-display ${size} ${strikethrough ? 'strikethrough' : ''}`}>
      <FontAwesomeIcon icon={faCoins} />
      <span className="cost-value">{amount?.toLocaleString()}</span>
    </span>
  );
}

/**
 * Token Change Indicator (for showing gains/losses)
 */
export function TokenChange({ amount, showIcon = true }) {
  const isPositive = amount >= 0;
  
  return (
    <span className={`token-change ${isPositive ? 'positive' : 'negative'}`}>
      {showIcon && <FontAwesomeIcon icon={faCoins} />}
      <span>{isPositive ? '+' : ''}{amount?.toLocaleString()}</span>
    </span>
  );
}

export default TokenBalance;
